import { api } from "./api";

const STORE = {
  repair: "pos_wizard_repair_catalog_v1",
  website: "pos_wizard_website_catalog_v1",
  order: "pos_wizard_website_order_v1",
  manufacturerOrder: "pos_wizard_mfr_order_v1",
  deviceOrder: "pos_wizard_device_order_v1",
};

const TTL = 10 * 60 * 1000;
let warmPromise = null;

function read(key, fallback) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - Number(parsed.ts || 0) > TTL) return fallback;
    return parsed.data ?? fallback;
  } catch {
    return fallback;
  }
}

function write(key, data) {
  try { sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data })); } catch { /* ignore */ }
}

export function getCachedWizardCatalog() {
  return {
    repairCatalog: read(STORE.repair, null),
    websiteCatalog: read(STORE.website, null),
    websiteOrders: read(STORE.order, null),
    manufacturerOrder: read(STORE.manufacturerOrder, null),
    deviceOrder: read(STORE.deviceOrder, null),
  };
}

export function invalidateWizardCatalogCache() {
  Object.values(STORE).forEach((key) => {
    try { sessionStorage.removeItem(key); } catch { /* ignore */ }
  });
  warmPromise = null;
}

export function prefetchWizardCatalog({ force = false } = {}) {
  const cached = getCachedWizardCatalog();
  const hasAll = cached.repairCatalog && cached.websiteCatalog && cached.websiteOrders;
  if (!force && hasAll) return Promise.resolve(cached);
  if (warmPromise && !force) return warmPromise;
  warmPromise = Promise.allSettled([
    api.get("/repair-catalog"),
    api.get("/catalog/website"),
    api.get("/catalog/website/order/all"),
    api.get("/repair-catalog/list-order", { params: { kind: "manufacturer" } }),
    api.get("/repair-catalog/list-order", { params: { kind: "device_type" } }),
  ]).then((results) => {
    const [repair, website, order, mfrOrder, devOrder] = results;
    if (repair.status === "fulfilled") write(STORE.repair, repair.value.data || {});
    if (website.status === "fulfilled") write(STORE.website, website.value.data || { manufacturers: [] });
    if (order.status === "fulfilled") write(STORE.order, order.value.data || {});
    if (mfrOrder.status === "fulfilled") write(STORE.manufacturerOrder, mfrOrder.value.data?.ordered_labels || []);
    if (devOrder.status === "fulfilled") write(STORE.deviceOrder, devOrder.value.data?.ordered_labels || []);
    return getCachedWizardCatalog();
  }).finally(() => { warmPromise = null; });
  return warmPromise;
}

export function cacheWizardCatalogPart(part, data) {
  const key = STORE[part];
  if (key) write(key, data);
}